import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import InterviewSession from '../components/InterviewSession';
import API from '../api/api';
import {
    VIDEO_SESSION_LABELS,
    VIDEO_SUBMISSIONS,
    videoSessionPath,
} from '../constants/workspaceLabels';
import { getTrialFraming } from '../utils/trialFraming';

/**
 * The recording session for one of a trial's video submissions.
 *
 * The route, the API prefix and the stored keys all still say `interview`, and
 * they stay that way: they are in live links, in emails and in records, and
 * renaming a stored key orphans the record behind it. What changes is the
 * reading. For a trial whose engine is eval4 this page hands `InterviewSession`
 * the workspace's words, points its two exits back at `/workspace/:id/video`, and
 * renders no Appli chrome around it — a workspace session framed by the product's
 * own navigation would be two worlds in one viewport. For every other trial it
 * renders exactly what it rendered before: same nav, same copy, same exits.
 *
 * Which of the two is decided per trial by the one field, `WorkTrial.eval_engine`,
 * read the same way `pages/WorkTrialRoute.js` reads it. Nothing renders until the
 * answer is in: mounting first and re-labelling after would show a candidate the
 * replaced word, and a word withdrawn after it has been read is not a rename.
 */
const Interview = () => {
    const { workTrialId, interviewId } = useParams();
    const [trial, setTrial] = useState(null);
    const [resolved, setResolved] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setResolved(false);
        setTrial(null);

        API.get(`work-trials/${workTrialId}`)
            .then((response) => {
                if (cancelled) return;
                setTrial(response.data?.work_trial || null);
                setResolved(true);
            })
            .catch(() => {
                // A trial we could not read is not a trial we may guess a workspace
                // for: the session below has its own error state, and the words it
                // reports it in are the ones it has always used.
                if (!cancelled) setResolved(true);
            });

        return () => { cancelled = true; };
    }, [workTrialId]);

    const isWorkspace = trial?.eval_engine === 'eval4';

    // API endpoints configuration for regular interviews
    const apiEndpoints = useMemo(() => ({
        fetch: `interviews/${workTrialId}/${interviewId}/`,
        start: `interviews/${workTrialId}/${interviewId}/start`,
        startQuestion: `interviews/${workTrialId}/${interviewId}/start_question/:questionNumber`,
        submitQuestion: `interviews/${workTrialId}/${interviewId}/submit_question/:questionNumber`,
        end: `interviews/${workTrialId}/${interviewId}/end`,
        proctor: `interviews/${workTrialId}/${interviewId}/proctor-events`
    }), [workTrialId, interviewId]);

    // Navigation configuration. The workspace variant's copy is framing-voiced —
    // a job trial's desk and a cycle's are not the same people — and both of its
    // exits are the one screen this session was opened from.
    const navigationConfig = useMemo(() => {
        if (!isWorkspace) {
            return {
                backPath: `/work-trial/${workTrialId}`,
                backText: "Back to Work Trial",
                successPath: `/work-trial/${workTrialId}`,
                successButtonText: "Return to Work Trial",
                successMessage: "Interview completed successfully! You can now return to the work trial page."
            };
        }

        const { videoSession } = getTrialFraming(trial).workspace;
        const back = videoSessionPath(workTrialId);

        return {
            backPath: back,
            backText: videoSession.backText,
            successPath: back,
            successButtonText: videoSession.successButtonText,
            successMessage: videoSession.successMessage,
        };
    }, [isWorkspace, trial, workTrialId]);

    if (!resolved) return null;

    const session = (
        <InterviewSession
            interviewId={interviewId}
            workTrialId={workTrialId}
            apiEndpoints={apiEndpoints}
            navigationConfig={navigationConfig}
            interviewType={isWorkspace ? VIDEO_SUBMISSIONS.one : "Interview Session"}
            labels={isWorkspace ? VIDEO_SESSION_LABELS : null}
        />
    );

    // No `<Navigation/>` inside a workspace session: the two-worlds boundary runs
    // between the employer's workspace and Appli's own product chrome, and this
    // session is on the workspace's side of it.
    if (isWorkspace) return session;

    return (
        <>
            <Navigation />
            {session}
        </>
    );
};

export default Interview;
